import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { EventsService } from "../../../_services/eventsServices/events.service";
import { FormGroup, FormGroupDirective } from "@angular/forms";

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";

import { sleep } from "../../../_helpers/sleep";
import { EventSerializerService } from "../../../_services/eventsServices/eventSerializer.service";
import { Subject } from "rxjs/Subject";
import { Observable } from "rxjs/Observable";

import { EventFormDynamicField } from "../../events-screen/event-page-models/event-form-dynamic-field";
import { EventFormInitialData } from "../../events-screen/event-page-models/event-form-initial-data";
import { ParentEventIdType } from "../../events-screen/event-page-models/parent-event-id-type";
import { UserManagementFormType } from "../user-management-models/user-management-form-type";
import { TranslationService } from "../../../_services/translation.service";
import { ITranslations } from "../../../_models/ITranslations";
import { UserManagementService } from "../../../_services/user-management.service";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { GeneralFieldValidationStatus } from "../../../common/async-validation-indicator/async-validation-indicator.component";

import { userManagementFormPersonalDetailsMessages } from "./user-management-form-personal-details-messages";
import { UserFormErrorsEnum } from "../user-management-models/user-form-errors.enum";

const WAIT_TIME_SPINNER_ANIMATION = 500;

@Component({
  selector: "user-management-form-personal-details",
  templateUrl: "./user-management-form-personal-details.component.html",
  styleUrls: [
    "../../../form/generic-form/generic-form.component.scss",
    "./user-management-form-personal-details.component.scss",
  ],
  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          height: "90px",
          maxHeight: "2000px",
          overflow: "visible",
          opacity: 1,
        })
      ),
      state(
        "closed",
        style({
          height: "1px",
          opacity: 0,
          overflow: "hidden",
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("* => open", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class UserManagementFormPersonalDetailsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input("eventFormCustomFormType")
  public eventFormCustomFormType: UserManagementFormType =
    UserManagementFormType.Standard;
  @Input("userId")
  public userId: "new" | string = "new";
  public personalDetailsForm: FormGroup;
  @Input("initialFormValuesOnEdit")
  public initialFormValuesOnEdit = {
    israeliId: "",
    email: "",
    phoneNumber: "",
    userName: "",
  };
  public userNameValidationStatus: GeneralFieldValidationStatus =
    GeneralFieldValidationStatus.NoInfo;
  public phoneNumberValidationStatus: GeneralFieldValidationStatus =
    GeneralFieldValidationStatus.NoInfo;
  public emailValidationStatus: GeneralFieldValidationStatus =
    GeneralFieldValidationStatus.NoInfo;
  public israeliIdValidationStatus: GeneralFieldValidationStatus =
    GeneralFieldValidationStatus.NoInfo;
  public dynamicFieldsChanges: Observable<EventFormDynamicField[]> =
    this.eventsService.getEventFormDynamicFields();
  public translations$: Observable<ITranslations>;
  public readonly userManagementFormPersonalDetailsMessages =
    userManagementFormPersonalDetailsMessages;
  public readonly GeneralFieldValidationStatus = GeneralFieldValidationStatus;
  private readonly destroy$ = new Subject<void>();
  private fieldChangesObserver: Subject<{ field: string; event: any }> =
    new Subject();

  constructor(
    private translationService: TranslationService,
    private eventsService: EventsService,
    private usesManagementService: UserManagementService,
    private userPersonalDetailsForm: FormGroupDirective
  ) {
    this.translations$ = this.translationService.translations;
    this.fieldChangesObserver
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(this.fieldChangeHandlerAfterDebounce);
  }
  public revalidateForm() {
    const formValues = this.personalDetailsForm.value;
    ["lastName", "firstName"].forEach((field) => {
      this.personalDetailsForm.controls[field].markAsTouched();
    });
    ["israeliId", "email", "phoneNumber", "userName"].forEach((field) => {
      if (!formValues[field] || formValues[field].trim() === "") {
        this.fieldChangeHandlerAfterDebounce({
          field,
          event: { target: { value: "" } },
        }).then();
      }
    });
  }

  public ngOnInit(): void {
    this.personalDetailsForm = this.userPersonalDetailsForm.form;
    this.buildForm();
  }

  public ngAfterViewInit() {}

  public isNewUser(): boolean {
    return UserManagementService.isNewUser(this.userId);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public inputFieldsChangeActionHandler($event: Event, fieldName: string) {
    this.fieldChangesObserver.next({ field: fieldName, event: $event });
  }
  private updateErrors(
    errorType: UserFormErrorsEnum,
    status: "hasError" | "valid"
  ) {
    const errorObject = {
      // ...(this.personalDetailsForm.errors || {}),
      [errorType]: status === "hasError" ? errorType : undefined,
    };
    let controlName = "";
    switch (errorType) {
      case UserFormErrorsEnum.BadIsraeliId:
        controlName = "israeliId";
        break;
      case UserFormErrorsEnum.BadEmail:
        controlName = "email";
        break;
      case UserFormErrorsEnum.BadUserName:
        controlName = "userName";
        break;
      case UserFormErrorsEnum.BadPhoneNumber:
        controlName = "phoneNumber";
        break;
    }
    if (controlName === "") {
      return;
    }

    this.personalDetailsForm.controls[controlName].setErrors({
      ...errorObject,
    });
  }

  private fieldChangeHandlerAfterDebounce = async (event: {
    field: string;
    event: any;
  }) => {
    const fieldValue = event.event.target["value"] as string;
    switch (event.field) {
      case "userName":
        const field = this.personalDetailsForm.controls["userName"];
        if (field.touched && fieldValue.length < 3) {
          this.updateErrors(UserFormErrorsEnum.BadUserName, "hasError");
          return;
        }
        if (
          this.initialFormValuesOnEdit.userName &&
          fieldValue.trim() === this.initialFormValuesOnEdit.userName
        ) {
          this.userNameValidationStatus = GeneralFieldValidationStatus.NoInfo;

          this.updateErrors(UserFormErrorsEnum.BadUserName, "valid");
          return;
        }
        if (!fieldValue.trim()) {
          this.userNameValidationStatus = GeneralFieldValidationStatus.Missing;

          this.updateErrors(UserFormErrorsEnum.BadUserName, "hasError");
          return;
        }
        if (!UserManagementService.validateUserName(fieldValue)) {
          this.userNameValidationStatus =
            GeneralFieldValidationStatus.InvalidStructure;
          this.updateErrors(UserFormErrorsEnum.BadUserName, "hasError");

          return;
        }
        this.userNameValidationStatus = GeneralFieldValidationStatus.Pending;
        const isFieldAvailable =
          (await this.usesManagementService.checkForFieldAvailability(
            { search: fieldValue },
            "userName"
          )) === "available";
        await sleep(WAIT_TIME_SPINNER_ANIMATION);
        if (event.event.target["value"] !== fieldValue) {
          return;
        }
        this.userNameValidationStatus = !isFieldAvailable
          ? (this.userNameValidationStatus =
              GeneralFieldValidationStatus.Invalid)
          : (this.userNameValidationStatus =
              GeneralFieldValidationStatus.Valid);
        this.updateErrors(
          UserFormErrorsEnum.BadUserName,
          this.userNameValidationStatus === GeneralFieldValidationStatus.Valid
            ? "valid"
            : "hasError"
        );

        break;
      case "phoneNumber":
        if (!fieldValue.trim()) {
          this.phoneNumberValidationStatus =
            GeneralFieldValidationStatus.Missing;
          this.updateErrors(UserFormErrorsEnum.BadPhoneNumber, "hasError");
          return;
        }
        if (!UserManagementService.validatePhoneNumber(fieldValue)) {
          this.phoneNumberValidationStatus =
            GeneralFieldValidationStatus.InvalidStructure;
          this.updateErrors(UserFormErrorsEnum.BadPhoneNumber, "hasError");

          return;
        }
        if (
          this.initialFormValuesOnEdit.phoneNumber &&
          fieldValue.trim() === this.initialFormValuesOnEdit.phoneNumber
        ) {
          this.phoneNumberValidationStatus =
            GeneralFieldValidationStatus.NoInfo;

          this.updateErrors(UserFormErrorsEnum.BadPhoneNumber, "valid");
          return;
        }
        this.phoneNumberValidationStatus = GeneralFieldValidationStatus.Pending;
        const isPhoneNumberAvailable =
          (await this.usesManagementService.checkForFieldAvailability(
            { search: fieldValue },
            "phoneNumber"
          )) === "available";
        await sleep(WAIT_TIME_SPINNER_ANIMATION);
        if (event.event.target["value"] !== fieldValue) {
          return;
        }
        this.phoneNumberValidationStatus = !isPhoneNumberAvailable
          ? (this.phoneNumberValidationStatus =
              GeneralFieldValidationStatus.Invalid)
          : (this.phoneNumberValidationStatus =
              GeneralFieldValidationStatus.Valid);
        this.updateErrors(
          UserFormErrorsEnum.BadPhoneNumber,
          this.phoneNumberValidationStatus ===
            GeneralFieldValidationStatus.Valid
            ? "valid"
            : "hasError"
        );
        break;
      case "email":
        if (!fieldValue.trim()) {
          this.emailValidationStatus = GeneralFieldValidationStatus.Missing;
          this.updateErrors(UserFormErrorsEnum.BadEmail, "hasError");

          return;
        }
        if (!UserManagementService.validateEmail(fieldValue)) {
          this.emailValidationStatus =
            GeneralFieldValidationStatus.InvalidStructure;
          this.updateErrors(UserFormErrorsEnum.BadEmail, "hasError");
          return;
        }
        if (
          this.initialFormValuesOnEdit.email &&
          fieldValue.trim() === this.initialFormValuesOnEdit.email
        ) {
          this.emailValidationStatus = GeneralFieldValidationStatus.NoInfo;

          this.updateErrors(UserFormErrorsEnum.BadEmail, "valid");
          return;
        }
        this.emailValidationStatus = GeneralFieldValidationStatus.Pending;
        const isEmailAvailable =
          (await this.usesManagementService.checkForFieldAvailability(
            { search: fieldValue },
            "email"
          )) === "available";
        await sleep(WAIT_TIME_SPINNER_ANIMATION);
        if (event.event.target["value"] !== fieldValue) {
          return;
        }
        this.emailValidationStatus = !isEmailAvailable
          ? (this.emailValidationStatus = GeneralFieldValidationStatus.Invalid)
          : (this.emailValidationStatus = GeneralFieldValidationStatus.Valid);

        this.updateErrors(
          UserFormErrorsEnum.BadEmail,
          this.emailValidationStatus === GeneralFieldValidationStatus.Valid
            ? "valid"
            : "hasError"
        );
        break;
      case "israeliId":
        if (!fieldValue.trim()) {
          this.israeliIdValidationStatus = GeneralFieldValidationStatus.Missing;
          this.updateErrors(UserFormErrorsEnum.BadIsraeliId, "hasError");
          return;
        }
        if (!UserManagementService.validateIsraeliId(fieldValue)) {
          this.israeliIdValidationStatus =
            GeneralFieldValidationStatus.InvalidStructure;
          this.updateErrors(UserFormErrorsEnum.BadIsraeliId, "hasError");
          return;
        }
        if (
          this.initialFormValuesOnEdit.israeliId &&
          fieldValue.trim() === this.initialFormValuesOnEdit.israeliId
        ) {
          this.israeliIdValidationStatus = GeneralFieldValidationStatus.NoInfo;
          this.updateErrors(UserFormErrorsEnum.BadIsraeliId, "valid");
          return;
        }
        this.israeliIdValidationStatus = GeneralFieldValidationStatus.Pending;
        const isIdAvailable =
          (await this.usesManagementService.checkForFieldAvailability(
            { search: fieldValue },
            "israeliId"
          )) === "available";
        await sleep(WAIT_TIME_SPINNER_ANIMATION);
        if (event.event.target["value"] !== fieldValue) {
          return;
        }
        this.israeliIdValidationStatus = !isIdAvailable
          ? (this.israeliIdValidationStatus =
              GeneralFieldValidationStatus.Invalid)
          : (this.israeliIdValidationStatus =
              GeneralFieldValidationStatus.Valid);
        this.updateErrors(
          UserFormErrorsEnum.BadIsraeliId,
          this.israeliIdValidationStatus === GeneralFieldValidationStatus.Valid
            ? "valid"
            : "hasError"
        );
        break;
    }
  };

  private buildForm(): void {}

  private async patchFormValues(eventFormInitialData: EventFormInitialData) {
    if (
      this.userPersonalDetailsForm.form &&
      this.userPersonalDetailsForm.form.controls
    ) {
    } else {
      await sleep(100);
    }

    const parentEvent = eventFormInitialData.eventFormParentEventData;
    const selectedEventType =
      eventFormInitialData.eventFormNoalData.event_types.find(
        (e) => e.is_selected
      );
    const brotherDiscountPeriod =
      eventFormInitialData.eventFormNoalData.registration_periods.find(
        (e) => e.is_selected
      );
    const parentEventId: ParentEventIdType =
      eventFormInitialData.eventFormParentEventData.id || "new";

    const valueToPatchEventDetails = {
      parentEventId,
      eventName: parentEvent.name,
      eventType: selectedEventType
        ? {
            customValue: selectedEventType.id,
            icon: null,
            key: selectedEventType.id,
            text: selectedEventType.name,
          }
        : "",
      eventStartDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_start_date
      ).datePicker,
      eventStartHour: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_start_date
      ).hoursPicker,
      eventEndDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_end_date
      ).datePicker,
      eventEndHour: EventSerializerService.EpocToDateAndTime(
        parentEvent.event_end_date
      ).hoursPicker,
      registrationStartDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.registration_start_date
      ).datePicker,
      registrationEndDate: EventSerializerService.EpocToDateAndTime(
        parentEvent.registration_end_date
      ).datePicker,
      eventDescription: parentEvent.description,
      closedToMembers: parentEvent.tax_payers_only,
      brothersDiscount: parentEvent.has_brother_discount,
      brothersDiscountPeriod: brotherDiscountPeriod
        ? {
            customValue: brotherDiscountPeriod.id,
            icon: null,
            key: brotherDiscountPeriod.id,
            text: brotherDiscountPeriod.name,
          }
        : "",
    };

    this.userPersonalDetailsForm.form.controls["parentEventDetails"].patchValue(
      valueToPatchEventDetails
    );

    const valueToPatchAdvancedDetails = {
      eventCode: parentEvent.code,
      eventIndex: parentEvent.priority_index,
      gainCenter1: parentEvent.merkaz_revach_1,
      gainCenter3: parentEvent.merkaz_revach_3,
    };
    this.userPersonalDetailsForm.form.controls[
      "parentEventAdvancedDetails"
    ].patchValue(valueToPatchAdvancedDetails);
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/users/user-management-form-personal-details/user-management-form-personal-details.component.ts